"use client";

import { useAuth } from "@/lib/auth";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { ACTION_LABELS, NAV_LABELS } from "@/lib/labels";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface DashboardStats {
  quotations: number;
  proposals: number;
  orders: number;
  customers: number;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Home() {
  const { user } = useAuth();

  const { data: stats } = useQuery<DashboardStats>({
    queryKey: ["dashboard-stats"],
    queryFn: async () => {
      const [quotations, proposals, orders, customers] = await Promise.all([
        api.get<{ total: number }>("/quotations/?limit=1").then((r) => r.total).catch(() => 0),
        api.get<{ total: number }>("/proposals/?limit=1").then((r) => r.total).catch(() => 0),
        api.get<{ total: number }>("/orders/?limit=1").then((r) => r.total).catch(() => 0),
        api.get<{ total: number }>("/customers/?limit=1").then((r) => r.total).catch(() => 0),
      ]);
      return { quotations, proposals, orders, customers };
    },
    staleTime: 30_000,
  });

  return (
    <main
      className="min-h-screen p-8"
      style={{ background: "var(--color-background)" }}
    >
      <div className="max-w-5xl mx-auto">
        {/* Welcome */}
        <div style={{ marginBottom: "32px" }}>
          <h1
            className="text-2xl font-bold"
            style={{ color: "var(--color-primary)" }}
          >
            {user ? `Welcome, ${user.name}` : "Quotes Platform"}
          </h1>
          <p
            className="text-sm mt-1"
            style={{ color: "var(--color-muted)" }}
          >
            Manufacturing quotation and estimation platform
          </p>
        </div>

        {/* Stats cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "16px",
            marginBottom: "32px",
          }}
        >
          <StatCard
            label="Quotations"
            value={stats?.quotations}
            href="/quotations"
            color="#00205B"
          />
          <StatCard
            label="Proposals"
            value={stats?.proposals}
            href="/proposals"
            color="#003DA5"
          />
          <StatCard
            label="Orders"
            value={stats?.orders}
            href="/orders"
            color="#0070D4"
          />
          <StatCard
            label="Customers"
            value={stats?.customers}
            href="/customers"
            color="#15803d"
          />
        </div>

        {/* Quick actions */}
        <div style={{ marginBottom: "32px" }}>
          <h2
            className="text-sm font-semibold uppercase tracking-wider"
            style={{ color: "var(--color-muted)", marginBottom: "12px" }}
          >
            Quick Actions
          </h2>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <ActionButton href="/quotations">{ACTION_LABELS.newQuotation}</ActionButton>
            <ActionButton href="/customers">{NAV_LABELS.customers}</ActionButton>
            <ActionButton href="/machines">{NAV_LABELS.machines}</ActionButton>
            <ActionButton href="/materials">{NAV_LABELS.materials}</ActionButton>
          </div>
        </div>

        {/* Footer */}
        <p
          className="text-xs"
          style={{ color: "var(--color-border)", marginTop: "48px" }}
        >
          v0.1.191
        </p>
      </div>
    </main>
  );
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function StatCard({
  label,
  value,
  href,
  color,
}: {
  label: string;
  value: number | undefined;
  href: string;
  color: string;
}): React.ReactElement {
  return (
    <a
      href={href}
      style={{
        display: "block",
        padding: "20px",
        borderRadius: "10px",
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        textDecoration: "none",
        transition: "box-shadow 0.15s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <p
        style={{
          fontSize: "12px",
          color: "var(--color-muted)",
          margin: 0,
          textTransform: "uppercase",
          fontWeight: 600,
          letterSpacing: "0.5px",
        }}
      >
        {label}
      </p>
      <p
        style={{
          fontSize: "32px",
          fontWeight: 700,
          color,
          margin: "4px 0 0 0",
        }}
      >
        {value !== undefined ? value : "-"}
      </p>
    </a>
  );
}

function ActionButton({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <a
      href={href}
      className="rounded-lg font-semibold text-sm transition-opacity hover:opacity-90"
      style={{
        padding: "10px 20px",
        background: "var(--color-surface)",
        color: "var(--color-text)",
        border: "1px solid var(--color-border)",
        textDecoration: "none",
      }}
    >
      {children}
    </a>
  );
}
